import React, { useState, useRef } from "react";
import { Modal, Form, Spinner } from "react-bootstrap";
import { Upload, Film, X } from "lucide-react";
import { AppButton } from "../ui";
import apiClient from "../../utils/apiClient";
import axios from "axios";
import "./Videos.scss";

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1073741824).toFixed(2)} GB`;
};

const UploadVideoModal = ({ show, onClose, batchId, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [topic, setTopic] = useState("");
  const [recordedAt, setRecordedAt] = useState("");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const resetForm = () => {
    setFile(null);
    setTitle("");
    setTopic("");
    setRecordedAt("");
    setDescription("");
    setUploading(false);
    setUploadProgress(0);
    setError("");
    setDragActive(false);
  };

  const handleClose = () => {
    if (uploading) return;
    resetForm();
    onClose();
  };

  const handleFileChange = (selectedFile) => {
    if (!selectedFile) return;
    const validTypes = ["video/mp4", "video/webm", "video/quicktime", "video/x-msvideo"];
    if (!validTypes.includes(selectedFile.type)) {
      setError("Please select a valid video file (MP4, WebM, MOV, AVI).");
      return;
    }
    setError("");
    setFile(selectedFile);

    // Auto-fill title from filename if empty
    if (!title) {
      const name = selectedFile.name.replace(/\.[^/.]+$/, "").replace(/[-_]/g, " ");
      setTitle(name);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFileChange(e.dataTransfer.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !title.trim() || !recordedAt) {
      setError("Video file, title, and recorded date are required.");
      return;
    }

    setUploading(true);
    setError("");
    setUploadProgress(0);

    try {
      // Upload video via server-side upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('batchId', batchId);
      formData.append('title', title.trim());
      formData.append('description', description.trim());
      formData.append('recordedAt', recordedAt);
      formData.append('topic', topic.trim());
      formData.append('fileType', file.type);
      formData.append('fileName', file.name);

      const { data: uploadData } = await apiClient.post("/videos/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        },
      });

      if (!uploadData.success) {
        setError(uploadData.message || "Failed to upload.");
        setUploading(false);
        return;
      }

      resetForm();
      onClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      const msg = err?.response?.data?.message || "Upload failed. Please try again.";
      setError(msg);
    }
    setUploading(false);
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      className="upload-video-modal"
      backdrop={uploading ? "static" : true}
    >
      <Modal.Header closeButton={!uploading}>
        <Modal.Title className="fs-6 fw-semibold d-flex align-items-center gap-2">
          <Upload size={18} />
          Upload Video
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <div className="alert alert-danger py-2 mb-3" role="alert" style={{ fontSize: "0.8125rem" }}>
              {error}
            </div>
          )}

          {/* File Dropzone */}
          {!file ? (
            <div
              className={`upload-video-modal__dropzone ${dragActive ? "upload-video-modal__dropzone--active" : ""}`}
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="upload-video-modal__dropzone-icon">
                <Upload />
              </div>
              <div className="upload-video-modal__dropzone-text">
                Drag &amp; drop a video file or click to browse
              </div>
              <div className="upload-video-modal__dropzone-hint">
                MP4, WebM, MOV, AVI • Max 5 GB
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                className="d-none"
                onChange={(e) => handleFileChange(e.target.files[0])}
              />
            </div>
          ) : (
            <div className="upload-video-modal__file-preview mb-3">
              <div className="upload-video-modal__file-preview-icon">
                <Film size={24} />
              </div>
              <div className="upload-video-modal__file-preview-info">
                <div className="upload-video-modal__file-preview-name">{file.name}</div>
                <div className="upload-video-modal__file-preview-size">{formatFileSize(file.size)}</div>
              </div>
              {!uploading && (
                <button
                  type="button"
                  className="upload-video-modal__file-preview-remove"
                  onClick={() => setFile(null)}
                >
                  <X size={16} />
                </button>
              )}
            </div>
          )}

          {/* Form Fields */}
          <div className="row g-3 mt-1">
            <div className="col-12">
              <Form.Group>
                <Form.Label className="small fw-medium">Title *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter video title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  disabled={uploading}
                  size="sm"
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="small fw-medium">Recorded Date *</Form.Label>
                <Form.Control
                  type="date"
                  value={recordedAt}
                  onChange={(e) => setRecordedAt(e.target.value)}
                  required
                  disabled={uploading}
                  size="sm"
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="small fw-medium">Topic</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., Arrays & Strings"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  disabled={uploading}
                  size="sm"
                />
              </Form.Group>
            </div>

            <div className="col-12">
              <Form.Group>
                <Form.Label className="small fw-medium">Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Optional description..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={uploading}
                  size="sm"
                />
              </Form.Group>
            </div>
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="mt-3">
              <div className="d-flex justify-content-between mb-1">
                <span className="small text-muted">Uploading...</span>
                <span className="small fw-medium">{uploadProgress}%</span>
              </div>
              <div className="progress" style={{ height: "6px" }}>
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{ width: `${uploadProgress}%` }}
                  aria-valuenow={uploadProgress}
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <AppButton
            variant="outline"
            size="sm"
            type="button"
            onClick={handleClose}
            disabled={uploading}
          >
            Cancel
          </AppButton>
          <AppButton
            variant="default"
            size="sm"
            type="submit"
            disabled={uploading || !file || !title.trim() || !recordedAt}
          >
            {uploading ? (
              <>
                <Spinner animation="border" size="sm" />
                Uploading...
              </>
            ) : (
              <>
                <Upload size={14} />
                Upload Video
              </>
            )}
          </AppButton>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UploadVideoModal;
